
import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { useLanguage } from '@/contexts/LanguageContext';

interface FiltersPanelProps {
  filters: {
    priceMax?: number;
    km?: number;
    leaseMonths?: number;
    gender?: 'any' | 'female' | 'male';
    furnished?: boolean;
    roommates?: boolean;
    sort?: 'distance' | 'price' | 'recency' | 'verified';
  };
  onFiltersChange: (filters: any) => void;
}

const FiltersPanel = ({ filters, onFiltersChange }: FiltersPanelProps) => {
  const { t } = useLanguage();

  const handleChange = (key: string, value: any) => {
    onFiltersChange({ [key]: value });
  };

  const clearFilters = () => {
    onFiltersChange({
      priceMax: undefined,
      km: undefined,
      leaseMonths: undefined,
      gender: undefined,
      furnished: undefined,
      roommates: undefined,
      sort: undefined
    });
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {/* Price */}
      <div className="space-y-2">
        <Label>{t('max_price', 'الحد الأقصى للسعر', 'Max Price')}</Label>
        <Input
          type="number"
          placeholder="EGP"
          value={filters.priceMax || ''}
          onChange={(e) => handleChange('priceMax', e.target.value ? parseInt(e.target.value) : undefined)}
        />
      </div>

      {/* Distance */}
      <div className="space-y-2">
        <Label>{t('max_distance', 'أقصى مسافة', 'Max Distance')}</Label>
        <Select value={filters.km?.toString() || 'any'} onValueChange={(value) => handleChange('km', value === 'any' ? undefined : parseInt(value))}>
          <SelectTrigger>
            <SelectValue placeholder={t('any_distance', 'أي مسافة', 'Any distance')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="any">{t('any_distance', 'أي مسافة', 'Any distance')}</SelectItem>
            <SelectItem value="1">1 {t('km', 'كم', 'km')}</SelectItem>
            <SelectItem value="2">2 {t('km', 'كم', 'km')}</SelectItem>
            <SelectItem value="5">5 {t('km', 'كم', 'km')}</SelectItem>
            <SelectItem value="10">10 {t('km', 'كم', 'km')}</SelectItem>
            <SelectItem value="20">20 {t('km', 'كم', 'km')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Lease Duration */}
      <div className="space-y-2">
        <Label>{t('lease_duration', 'مدة الإيجار', 'Lease Duration')}</Label>
        <Select value={filters.leaseMonths?.toString() || 'any'} onValueChange={(value) => handleChange('leaseMonths', value === 'any' ? undefined : parseInt(value))}>
          <SelectTrigger>
            <SelectValue placeholder={t('any_duration', 'أي مدة', 'Any duration')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="any">{t('any_duration', 'أي مدة', 'Any duration')}</SelectItem>
            <SelectItem value="3">3 {t('months', 'شهور', 'months')}</SelectItem>
            <SelectItem value="6">6 {t('months', 'شهور', 'months')}</SelectItem>
            <SelectItem value="12">12 {t('months', 'شهور', 'months')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Gender */}
      <div className="space-y-2">
        <Label>{t('gender_preference', 'تفضيل الجنس', 'Gender Preference')}</Label>
        <Select value={filters.gender || 'any'} onValueChange={(value) => handleChange('gender', value === 'any' ? undefined : value)}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="any">{t('any_gender', 'أي جنس', 'Any')}</SelectItem>
            <SelectItem value="female">{t('female_only', 'إناث فقط', 'Female only')}</SelectItem>
            <SelectItem value="male">{t('male_only', 'ذكور فقط', 'Male only')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Furnished */}
      <div className="flex items-center space-x-2">
        <Switch
          id="furnished"
          checked={filters.furnished || false}
          onCheckedChange={(checked) => handleChange('furnished', checked ? true : undefined)}
        />
        <Label htmlFor="furnished">{t('furnished', 'مفروش', 'Furnished')}</Label>
      </div>

      {/* Roommates */}
      <div className="flex items-center space-x-2">
        <Switch
          id="roommates"
          checked={filters.roommates || false}
          onCheckedChange={(checked) => handleChange('roommates', checked ? true : undefined)}
        />
        <Label htmlFor="roommates">{t('roommates_allowed', 'يسمح بزملاء السكن', 'Roommates allowed')}</Label>
      </div>

      {/* Sort */}
      <div className="space-y-2">
        <Label>{t('sort_by', 'ترتيب حسب', 'Sort by')}</Label>
        <Select value={filters.sort || 'distance'} onValueChange={(value) => handleChange('sort', value)}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="distance">{t('distance', 'المسافة', 'Distance')}</SelectItem>
            <SelectItem value="price">{t('price', 'السعر', 'Price')}</SelectItem>
            <SelectItem value="recency">{t('newest', 'الأحدث', 'Newest')}</SelectItem>
            <SelectItem value="verified">{t('verified', 'متحقق', 'Verified')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Clear Filters */}
      <div className="flex items-end">
        <Button variant="outline" onClick={clearFilters} className="w-full">
          {t('clear_filters', 'مسح الفلاتر', 'Clear Filters')}
        </Button>
      </div>
    </div>
  );
};

export default FiltersPanel;
